import React from 'react';
import { useDispatch } from 'react-redux';
import DropdownMenu from '../../../common/MenuDropdown';
import { IconButton } from '../../Payments/Modals/EditPayout/IconButton';
import {
    getTicketActionsList,
    MilestoneType,
    MilestoneSubStatus,
    TicketStatus,
    isIndividualMilestoneStatusInRange,
    canAcceptAsset,
    canRejectAsset,
    canApproveSignOffTicket,
    TicketAgentRole,
    canEditTicket,
} from '../TicketController';
import { openCustomModal } from '../../../actions/modal';
import RejectSignOff from '../Modal/RejectSignOff';
import { useNavigate } from 'react-router-dom';
import { ticket as ticketPath } from '../../../router/roleWisePageRoute';
import { RenderApproveSignOff } from '../../Payments/Modals/EditPayout';
import StatusLabel from '../../Payments/reusableComponents/StatusLabel';
import { useSelector } from 'react-redux';
import { UnlockSignoff } from '../Modal/UnlockSignoff';
import CustomerApproveRejectSignOff from '../Modal/CustomerApproveRejectSignOff';
import { ScogoID } from '../../../utils/common';
import PavAssetVerification from '../../../features/pav/PavAssetVerification';
import { getPavMilestoneLabel, isPavMilestone } from '../../../features/pav/pavConfig';

export const TicketAction = ({ ticket = {} }) => {
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const ticketActions = getTicketActionsList(ticket, newLoggedUserData);
    const baseConditionToHideDirectActions = ticket.milestones && ticket.milestones.every((milestone) => milestone.is_signoff_locked === false) && ![TicketStatus.CLOSED, TicketStatus.ON_HOLD].includes(ticket.status);
    const org_membership_ids = newLoggedUserData.orgMemberships.map((org_membership) => org_membership.id);

    const approveRejectAsset = baseConditionToHideDirectActions && ticket.milestones && ticket.milestones.every((milestone) => milestone.type === MilestoneType.LOGISTICS && isIndividualMilestoneStatusInRange(milestone, MilestoneSubStatus.NEW, MilestoneSubStatus.SIGN_OFF_ACCEPTED) && milestone.execution_flow_required === false);

    const milestonesToCheck = ticket.milestones ? ticket.milestones.filter((milestone) => ![MilestoneSubStatus.SIGN_OFF_REJECTED, MilestoneSubStatus.HARD_CLOSED].includes(milestone.milestone_status) && milestone.is_signoff_locked === false) : [];

    const isInStateToApproveOrReject = milestonesToCheck.length > 0
        && milestonesToCheck.every((milestone) => {
            if (ticket.is_customer_sign_off_required === true && ticket.org_id !== newLoggedUserData.org.id) {
                return isIndividualMilestoneStatusInRange(
                    milestone,
                    MilestoneSubStatus.CUSTOMER_SIGN_OFF_ACCEPTED,
                    MilestoneSubStatus.CUSTOMER_SIGN_OFF_ACCEPTED
                );
            } else {
                return isIndividualMilestoneStatusInRange(
                    milestone,
                    MilestoneSubStatus.SIGN_OFF_REQUESTED,
                    MilestoneSubStatus.SIGN_OFF_REQUESTED
                );
            }
        });


    // Default Approve Reject
    const approveReject = canApproveSignOffTicket(ticket.actions) && org_membership_ids.includes(ticket.helpdesk_id) && ![TicketStatus.CLOSED, TicketStatus.ON_HOLD].includes(ticket.status) && milestonesToCheck.length > 0 && milestonesToCheck.every((milestone) => {
        if (ticket.is_customer_sign_off_required === true && ticket.helpdesk_id === ScogoID) {
            return isIndividualMilestoneStatusInRange(
                milestone,
                MilestoneSubStatus.CUSTOMER_SIGN_OFF_ACCEPTED,
                MilestoneSubStatus.CUSTOMER_SIGN_OFF_ACCEPTED
            );
        } else {
            return isIndividualMilestoneStatusInRange(
                milestone,
                MilestoneSubStatus.SIGN_OFF_REQUESTED,
                MilestoneSubStatus.SIGN_OFF_REQUESTED
            );
        }
    });

    // Customer Approval or Reject
    const showCustomerApprovalOrReject = ticket.helpdesk_id === ScogoID && ticket.org_id === newLoggedUserData.org.id && ticket.is_customer_sign_off_required === true
        && canApproveSignOffTicket(ticket.actions) && ![TicketStatus.CLOSED, TicketStatus.ON_HOLD].includes(ticket.status) && milestonesToCheck.length > 0 && milestonesToCheck.every((milestone) => milestone.milestone_status === MilestoneSubStatus.SIGN_OFF_REQUESTED);

    const showUnlockSignoff = canEditTicket(ticket) && ticket.milestones.every((milestone) => milestone.is_signoff_locked === true || milestone.milestone_status === MilestoneSubStatus.HARD_CLOSED) && ![TicketStatus.CLOSED, TicketStatus.ON_HOLD].includes(ticket.status);


    const isLoginUserIsPSPClusterOrSP = isInStateToApproveOrReject && ticket.agents.find((agent) => {
        return [TicketAgentRole.PSP, TicketAgentRole.CLUSTER, TicketAgentRole.SP].includes(agent.role) && newLoggedUserData.user.id === agent.agent.id;
    });

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const onApproveClick = () => {
        navigate(`${ticketPath.path}/${ticket.id}/edit-payout?source=ticket`)
    };

    const onRejectClick = (e) => {
        const milestones = ticket.milestones.filter((milestone) => isIndividualMilestoneStatusInRange(milestone, MilestoneSubStatus.SIGN_OFF_REQUESTED, MilestoneSubStatus.SIGN_OFF_REQUESTED));
        dispatch(openCustomModal({
            modalComponent: <RejectSignOff ticket={ticket} milestones={milestones} />,
            heading: 'Reject Ticket',
            modalWidth: '50rem',
            modalHeight: 'auto',
        }));
    };

    const openPavSignoffReview = (pavMilestone) => {
        const ticketNumber = ticket?.ticket_number || ticket?.job_ticket_number;
        dispatch(openCustomModal({
            modalComponent: <PavAssetVerification ticket={ticket} milestone={pavMilestone} variant='modal' reviewSavedAssetsOnly onSignOffFormBack={() => openPavSignoffReview(pavMilestone)} />,
            heading: (
                <span className='text-black font-semibold'>
                    {getPavMilestoneLabel(pavMilestone)}
                    {ticketNumber ? <span className='text-scogobgsky font-semibold'>: {ticketNumber}</span> : ''}
                </span>
            ),
            modalWidth: 'min(82rem, 95vw)',
            modalHeight: 'auto',
            maxHeight: '84vh',
        }));
    };

    const onCustomerApproveRejectClick = (status) => {
        const milestones = ticket.milestones.filter((milestone) => isIndividualMilestoneStatusInRange(milestone, MilestoneSubStatus.SIGN_OFF_REQUESTED, MilestoneSubStatus.SIGN_OFF_REQUESTED) && milestone.is_signoff_locked === false);
        const pavMilestone = milestones.length === 1 && isPavMilestone(milestones[0]) ? milestones[0] : undefined;

        if (pavMilestone) {
            openPavSignoffReview(pavMilestone);
            return;
        }

        dispatch(openCustomModal({
            modalComponent: <CustomerApproveRejectSignOff ticket={ticket} milestones={milestones} status={status} />,
            heading: status === 'CUSTOMER_SIGN_OFF_ACCEPTED' ? 'Approve Signoff' : 'Reject Signoff',
            modalWidth: '50rem',
            modalHeight: 'auto',
        }));
    }


    const isTicketClosed = ticket.status === TicketStatus.CLOSED;
    const showPendingFromCustomer = ticket.org_id !== newLoggedUserData.org.id && ticket.is_customer_sign_off_required === true && ticket.milestones.some((milestone) => milestone.milestone_status === MilestoneSubStatus.SIGN_OFF_REQUESTED) && ticket.milestones.every((milestone) => milestone.milestone_status === MilestoneSubStatus.SIGN_OFF_REQUESTED || milestone.milestone_status === MilestoneSubStatus.HARD_CLOSED || milestone.milestone_status === MilestoneSubStatus.CUSTOMER_SIGN_OFF_ACCEPTED || milestone.milestone_status === MilestoneSubStatus.CUSTOMER_SIGN_OFF_REJECTED || milestone.milestone_status === MilestoneSubStatus.SIGN_OFF_REJECTED || milestone.milestone_status === MilestoneSubStatus.SIGN_OFF_ACCEPTED);

    let actionComponent = null;

    if (isTicketClosed) {
        return <div className='flex gap-2 items-center ticket_action' id='ticket_action_element'>
            <StatusLabel
                content={TicketStatus.CLOSED}
                labelClass={`bg-gray-200 border border-gray-400 text-black ticket_action_status`}
            />
        </div>
    }

    if (showPendingFromCustomer) {
        return <div className='flex gap-2 items-center ticket_action' id='ticket_action_element'>
            <StatusLabel
                content={'Pending from Customer'}
                labelClass={`bg-orange-50 text-scogoorange border border-orange-400 orange-gradient ticket_action_status`}
            />
        </div>
    }

    if (showUnlockSignoff) {
        actionComponent = <IconButton
            loader={false}
            onClick={() => {
                dispatch(openCustomModal({
                    modalComponent: <UnlockSignoff ticketId={ticket.id} milestones={ticket.milestones.filter((milestone) => milestone.is_signoff_locked)} agents={ticket.agents} />,
                    heading: 'Unlock Signoff',
                    modalHeight: 'auto',
                    modalWidth: '50rem'
                }));
            }}
            onHoverTitle={'Unlock Signoff'}
            iconName={'key'}
            backgroundColor={'bg-scogoLightRed text-scogoclosed border border-scogoclosed redlinear-gradient p-1'}
            loaderSize='12'
            divClassName='ticket_action_unlock_signoff'
        />
    }


    if (approveRejectAsset) {
        actionComponent = (
            <div className='flex gap-2 items-center' >
                {canAcceptAsset(ticket) && <RenderApproveSignOff selectedMilestone={ticket?.milestones?.[0]?.id} ticketId={ticket.id} formName={'accept_asset'} category={'APPROVAL'} org_id={ticket?.org_id} >
                    <IconButton
                        loader={false}
                        onClick={(e) => {
                            // console.log('e:', e)
                        }}
                        onHoverTitle={'Accept Asset'}
                        iconName={'done'}
                        backgroundColor={'bg-labelLightGreen text-scogo2e border border-scogo2e greenlinear-gradient'}
                        loaderSize='12'
                        divClassName='ticket_action_accept_asset'
                    />
                </RenderApproveSignOff>}
                {canRejectAsset(ticket) &&
                    <IconButton
                        loader={false}
                        onClick={(e) => onRejectClick(e)}
                        onHoverTitle={'Reject Asset'}
                        iconName={'close'}
                        backgroundColor={'bg-scogoLightRed text-scogoclosed border border-scogoclosed redlinear-gradient'}
                        loaderSize='12'
                        divClassName='ticket_action_reject_asset'
                    />
                }
            </div>
        );
    } else if (showCustomerApprovalOrReject) {
        actionComponent = (
            <div className='flex gap-2 items-center ticket_action_dropdown'>
                <IconButton
                    loader={false}
                    onClick={() => onCustomerApproveRejectClick('CUSTOMER_SIGN_OFF_ACCEPTED')}
                    onHoverTitle={'Aprrove Ticket'}
                    iconName={'done'}
                    backgroundColor={'bg-labelLightGreen text-scogo2e border border-scogo2e greenlinear-gradient'}
                    loaderSize='12'
                    divClassName='ticket_action_approve_ticket customer_approval_or_rejection'
                />
                <IconButton
                    loader={false}
                    onClick={() => onCustomerApproveRejectClick('CUSTOMER_SIGN_OFF_REJECTED')}
                    onHoverTitle={'Reject Ticket'}
                    iconName={'close'}
                    backgroundColor={'bg-scogoLightRed text-scogoclosed border border-scogoclosed redlinear-gradient'}
                    loaderSize='12'
                    divClassName='ticket_action_reject_ticket customer_approval_or_rejection'
                />
            </div>
        )
    } else if (approveReject) {
        actionComponent = (
            <div className='flex gap-2 items-center ticket_action_dropdown'>
                <IconButton
                    loader={false}
                    onClick={() => onApproveClick()}
                    onHoverTitle={'Aprrove Ticket'}
                    iconName={'done'}
                    backgroundColor={'bg-labelLightGreen text-scogo2e border border-scogo2e greenlinear-gradient'}
                    loaderSize='12'
                    divClassName='ticket_action_approve_ticket'
                />
                <IconButton
                    loader={false}
                    onClick={() => onRejectClick()}
                    onHoverTitle={'Reject Ticket'}
                    iconName={'close'}
                    backgroundColor={'bg-scogoLightRed text-scogoclosed border border-scogoclosed redlinear-gradient'}
                    loaderSize='12'
                    divClassName='ticket_action_reject_ticket'
                />
            </div>
        );
    } else if (isLoginUserIsPSPClusterOrSP) {
        actionComponent = <IconButton
            loader={false}
            onClick={() => onRejectClick()}
            onHoverTitle={'Reject Ticket'}
            iconName={'close'}
            backgroundColor={'bg-scogoLightRed text-scogoclosed border border-scogoclosed redlinear-gradient ticket_action_dropdown'}
            loaderSize='12'
            divClassName='ticket_action_reject_ticket ticket_action_dropdown'
        />
    } else {
        actionComponent = <DropdownMenu
            menuData={ticketActions}
            item={ticket}
            componentOrIcon={'more_vert'}
            payload={{
                navigate,
                dispatch
            }}
            className='ticket_action_dropdown'
        />
    }

    return <div className='flex gap-2 items-center ticket_action' id='ticket_action_element'>
        {actionComponent}
    </div>
};
